import { valeurDefaultValues } from 'src/components/client/prejudiceFormTypes/FormValeur';
import {
  calendrierAssistanceDefaultValues,
  CalendrierAssistanceExistingRow,
  CalendrierAssistanceExistingValues,
} from 'src/constants/prejudice/defaultValues/FormCalendrierAssistance.defaultValues';
import {
  calendrierAssistanceCapitalisationDefaultValues,
  CalendrierAssistanceCapitalisationExistingRow,
  CalendrierAssistanceCapitalisationExistingValues,
} from 'src/constants/prejudice/defaultValues/FormCalendrierAssistanceCapitalisation.defaultValues';
import {
  calendrierDeficitFonctionnelTemporairePartielDefaultValues,
  DFTPExistingValues,
} from 'src/constants/prejudice/defaultValues/FormCalendrierDeficitFonctionnelTemporairePartiel.defaultValues';
import { calendrierDeficitFonctionnelTemporaireTotalDefaultValues, DFTTExistingValues, DFTTRowExistingValues } from 'src/constants/prejudice/defaultValues/FormCalendrierDeficitFonctionnelTemporaireTotal.defaultValues';
import {
  calendrierDepenseDefaultValues,
  CalendrierDepenseExistingRow,
  CalendrierDepenseExistingValues,
} from 'src/constants/prejudice/defaultValues/FormCalendrierDepense.defaultValues';
import {
  calendrierDepenseCapitalisationDefaultValues,
  CalendrierDepenseCapitalisationExistingRow,
  CalendrierDepenseCapitalisationExistingValues,
} from 'src/constants/prejudice/defaultValues/FormCalendrierDepenseCapitalisation.defaultValues';
import {
  AgrementExistingValues,
  DfpExistingValues,
  listeProjectionDefaultValues,
  ListeProjectionExistingValues,
  MaladieExistingValues,
} from 'src/constants/prejudice/defaultValues/FormListeProjection.defaultValues';
import {
  PerteDeGainsProfessionnelsRowExistingValues,
  pgpaSaisieDirecteDefaultValues,
  PGPExistingValues,
  pgpfSaisieDirecteDefaultValues,
} from 'src/constants/prejudice/defaultValues/FormPerteGainsProfessionnels.defaultValues';
import { scolaireDefaultValues } from 'src/constants/prejudice/defaultValues/Formscolaire.defaultValues';
import { Bareme } from 'src/types/bareme.type';
import {
  ATPFType,
  ATPTType,
  BaremeCapitalisationType,
  dateSchema,
  DegreType,
  ExtractionOutputType,
  PrejudiceLabel,
} from 'src/types/extraction.schema';
import {
  AssistanceUniteNombreHeure,
  CreatePrejudiceDto,
  DepenseFrequence,
  EditDatesOptions,
  PGPFReliquatAndPGPFReliquatActivationStatus,
  PrejudiceFormBase,
  TypeAssistance,
} from 'src/types/prejudice.type';
import { Procedure } from 'src/types/procedure.type';
import { Victime } from 'src/types/victime.type';
import { date, z } from 'zod';
import { MonetaryErosion } from 'src/types/monetaryErosion.type';
import { getPGPFReliquatAndPGPFReliquatActivationStatus } from '../prejudices/pgpfReliquat';
import { DFTPRowExistingValues } from 'src/constants/prejudice/defaultValues/FormCalendrierDeficitFonctionnelTemporairePartiel.defaultValues';

type ParsedDate = {
  type: 'date' | 'existingDate';
  date: Date | null;
  isoDate: string | null;
  dateOption: EditDatesOptions | null;
};

export class ExtractionParser {
  parseDateMonth(month: z.infer<typeof dateSchema>['month']): number {
    switch (month) {
      case 'january':
        return 0;
      case 'february':
        return 1;
      case 'march':
        return 2;
      case 'april':
        return 3;
      case 'may':
        return 4;
      case 'june':
        return 5;
      case 'july':
        return 6;
      case 'august':
        return 7;
      case 'september':
        return 8;
      case 'october':
        return 9;
      case 'november':
        return 10;
      case 'december':
        return 11;
      default:
        return 0;
    }
  }

  parseDefaultDatesValues(
    parsedDateByKey: Record<string, ParsedDate | null>,
  ): PrejudiceFormBase['defaultDatesValues'] {
    return Object.entries(parsedDateByKey).reduce<PrejudiceFormBase['defaultDatesValues']>(
      (acc, [key, value]) => {
        if (value && value.type === 'existingDate' && value.dateOption) {
          acc[key] = value.dateOption;
        }
        return acc;
      },
      {},
    );
  }

  parseExistingDate(
    extractedExistingDate: z.infer<typeof dateSchema>['existingDate'] | null,
    {
      victime,
      procedure,
    }: {
      victime: Pick<Victime, 'dateAccident' | 'dateDeces' | 'dateNaissance'>;
      procedure: Pick<Procedure, 'dateConsolidation' | 'dateLiquidation'>;
    },
  ): {
    existingDateOption: EditDatesOptions | null;
    existingDate: Date | null;
    existingDateIsoDate: string | null;
  } | null {
    if (!extractedExistingDate) {
      return null;
    }
    switch (extractedExistingDate) {
      case 'dateDeNaissance': {
        const dateDeNaissance = victime.dateNaissance
          ? new Date(victime.dateNaissance)
          : null;
        return {
          existingDateOption: null,
          existingDate: dateDeNaissance,
          existingDateIsoDate: dateDeNaissance?.toISOString() ?? null,
        };
      }
      case 'dateDeDeces': {
        const dateDeDeces = victime.dateDeces
          ? new Date(victime.dateDeces)
          : null;
        return {
          existingDateOption: null,
          existingDate: dateDeDeces,
          existingDateIsoDate: dateDeDeces?.toISOString() ?? null,
        };
      }
      case 'dateAccident': {
        const dateAccident = victime.dateAccident
          ? new Date(victime.dateAccident)
          : null;
        return {
          existingDateOption: 'dateAccident',
          existingDate: dateAccident,
          existingDateIsoDate: dateAccident?.toISOString() ?? null,
        };
      }
      case 'dateLiquidation':
      case 'datePaiement': {
        const dateLiquidation = procedure.dateLiquidation
          ? new Date(procedure.dateLiquidation)
          : null;
        return {
          existingDateOption: 'dateLiquidation',
          existingDate: dateLiquidation,
          existingDateIsoDate: dateLiquidation?.toISOString() ?? null,
        };
      }
      case 'dateConsolidation': {
        const dateConsolidation = procedure.dateConsolidation
          ? new Date(procedure.dateConsolidation)
          : null;
        return {
          existingDateOption: 'dateConsolidation',
          existingDate: dateConsolidation,
          existingDateIsoDate: dateConsolidation?.toISOString() ?? null,
        };
      }
    }
  }

  parseDate(
    extractedDate: z.infer<typeof dateSchema> | null,
    {
      victime,
      procedure,
    }: {
      victime: Pick<Victime, 'dateAccident' | 'dateDeces' | 'dateNaissance'>;
      procedure: Pick<Procedure, 'dateConsolidation' | 'dateLiquidation'>;
    },
  ): ParsedDate | null {
    if (!extractedDate) {
      return null;
    }
    const existingDate = this.parseExistingDate(extractedDate.existingDate, {
      victime,
      procedure,
    });
    if (existingDate) {
      return {
        type: 'existingDate',
        date: existingDate.existingDate,
        isoDate: existingDate.existingDateIsoDate,
        dateOption: existingDate.existingDateOption,
      };
    }
    if (
      extractedDate.year &&
      extractedDate.month &&
      extractedDate.day &&
      extractedDate.definedFields.length === 3
    ) {
      const date = new Date(
        extractedDate.year,
        this.parseDateMonth(extractedDate.month),
        extractedDate.day,
      );
      return {
        type: 'date',
        date,
        isoDate: date.toISOString(),
        dateOption: null,
      };
    }
    return null;
  }

  parseSexe(sexe: 'male' | 'female' | 'unknown' | null): 'm' | 'f' | 'u' {
    switch (sexe) {
      case 'male':
        return 'm';
      case 'female':
        return 'f';
      default:
        return 'u';
    }
  }

  parseDegre(degre: DegreType | null): number {
    if (!degre) {
      return 0;
    }
    switch (degre) {
      case 'Hors norme':
        return 8;
      default:
        // degree looks like "1/7" and we need to return 1
        const degreValue = degre.split('/')[0];
        const parsedDegreValue = degreValue ? parseFloat(degreValue) : 0;
        if (isNaN(parsedDegreValue)) {
          return 0;
        }
        return parsedDegreValue;
    }
  }

  parseBaremeCapitalisation(
    baremes: Bareme[],
    baremeCapitalisationExtracted: BaremeCapitalisationType,
  ): string | null {
    const baremesCapitalisations = baremes.filter(
      (bareme) => bareme.type === 'Capitalisation',
    );
    switch (baremeCapitalisationExtracted.selectedBareme) {
      case 'BCRIV 2021':
        return (
          baremesCapitalisations.find(
            (bareme) => bareme.source === 'BCRIV' && bareme.year === 2021,
          )?._id ?? null
        );
      case 'BCRIV 2023':
        return (
          baremesCapitalisations.find(
            (bareme) => bareme.source === 'BCRIV' && bareme.year === 2023,
          )?._id ?? null
        );
      case 'BCRIV 2025':
        return (
          baremesCapitalisations.find(
            (bareme) => bareme.source === 'BCRIV' && bareme.year === 2025,
          )?._id ?? null
        );
      case 'Christian Jaumain':
        return (
          baremesCapitalisations.find((bareme) => bareme.source === 'CJ')
            ?._id ?? null
        );
      case 'ONIAM 2022':
        return (
          baremesCapitalisations.find(
            (bareme) => bareme.source === 'ONIAM' && bareme.year === 2022,
          )?._id ?? null
        );
      case 'Gazette du palais 2018 0,5%':
        return (
          baremesCapitalisations.find(
            (bareme) =>
              bareme.source === 'GDP' &&
              bareme.year === 2018 &&
              bareme.additionalProperties?.taux === 0.5,
          )?._id ?? null
        );
      case 'Gazette du palais 2020 0%':
        return (
          baremesCapitalisations.find(
            (bareme) =>
              bareme.source === 'GDP' &&
              bareme.year === 2020 &&
              bareme.additionalProperties?.taux === 0,
          )?._id ?? null
        );
      case 'Gazette du palais 2020 0,3%':
        return (
          baremesCapitalisations.find(
            (bareme) =>
              bareme.source === 'GDP' &&
              bareme.year === 2020 &&
              bareme.additionalProperties?.taux === 0.3,
          )?._id ?? null
        );
      case 'Gazette du palais 2022 -1%':
        return (
          baremesCapitalisations.find(
            (bareme) =>
              bareme.source === 'GDP' &&
              bareme.year === 2022 &&
              bareme.additionalProperties?.taux === -1,
          )?._id ?? null
        );
      case 'Gazette du palais 2022 0%':
        return (
          baremesCapitalisations.find(
            (bareme) =>
              bareme.source === 'GDP' &&
              bareme.year === 2022 &&
              bareme.additionalProperties?.taux === 0,
          )?._id ?? null
        );
      case 'Gazette du palais 2025 0,5% stationaire':
        return (
          baremesCapitalisations.find(
            (bareme) =>
              bareme.source === 'GDP' &&
              bareme.year === 2025 &&
              bareme.additionalProperties?.taux === 0.5 &&
              bareme.additionalProperties.typeTaux === 'stationnaire',
          )?._id ?? null
        );
      case 'Gazette du palais 2025 0,5% retrospectif':
        return (
          baremesCapitalisations.find(
            (bareme) =>
              bareme.source === 'GDP' &&
              bareme.year === 2025 &&
              bareme.additionalProperties?.taux === 0.5 &&
              bareme.additionalProperties.typeTaux === 'prospectif',
          )?._id ?? null
        );
      default:
        return null;
    }
  }
  parseTypeAssistance(
    assistanceRow: ATPTType['rows'][number] | ATPFType['rows'][number],
  ): TypeAssistance | string | undefined {
    if (!assistanceRow.typeAssistance) {
      return undefined;
    }
    if (
      assistanceRow.typeAssistance === 'autre' &&
      assistanceRow.typeAssistanceIfOther
    ) {
      return assistanceRow.typeAssistanceIfOther;
    }
    switch (assistanceRow.typeAssistance) {
      case 'aideFamilleOuProchesActive':
        return 'aideFamilleOuProchesActive';
      case 'aideFamilleOuProchesPassive':
        return 'aideFamilleOuProchesPassive';
      case 'aideProfessionnelleNonMedicaleActive':
        return 'aideProfiessionnelleNonMedicaleActive';
      case 'aideProfessionnelleNonMedicalePassive':
        return 'aideProfiessionnelleNonMedicalePassive';
      case 'aideProfessionnelleMedicaleActive':
        return 'aideProfiessionnelleMedicaleActive';
      case 'aideALaParentalite':
        return 'aideALaParentalite';
      case 'gestionDuPersonnelParLaVictime':
        return 'gestionDuPersonnelParLaVictime';
      case 'aideHumaine':
        return 'aideHumaine';
      default:
        return undefined;
    }
  }
  parseExtractionPrejudice = (
    procedure: Procedure,
    label: PrejudiceLabel,
    extraction: ExtractionOutputType,
    baremes: Bareme[],
    victime: Victime,
    monetaryErosions: MonetaryErosion[],
  ): CreatePrejudiceDto | null => {
    const dateConsolidation = procedure.dateConsolidation
      ? new Date(procedure.dateConsolidation)
      : undefined;
    const dateLiquidation = procedure.dateLiquidation
      ? new Date(procedure.dateLiquidation)
      : undefined;
    const PGPFReliquat: PGPFReliquatAndPGPFReliquatActivationStatus = {
      PGPFReliquat: null,
      activatePGPFReliquat: null,
    };
    const parsedBaremeCapitalisationId: string | undefined = extraction
      .prejudices.baremeCapitalisation
      ? (this.parseBaremeCapitalisation(
          baremes,
          extraction.prejudices.baremeCapitalisation,
        ) ?? undefined)
      : undefined;
    switch (label) {
      case 'deficitFonctionnelTemporairePartiel': {
        const defaultValues =
          extraction.prejudices.deficitFonctionnelTemporairePartiel?.rows.reduce<DFTPExistingValues>(
            (accumulator, row, index) => {
              const dateDebut = this.parseDate(row.dateDebut, {
                victime,
                procedure,
              });
              const dateFin = this.parseDate(row.dateFin, {
                victime,
                procedure,
              });
              const percentageByClasse = {
                'Classe I': 10,
                'Classe II': 25,
                'Classe III': 50,
                'Classe IV': 75,
              } as const;
              const percentage = row.classeDFTP
                ? percentageByClasse[row.classeDFTP]
                : !!row.tauxDFTP
                  ? row.tauxDFTP
                  : null;
              const newDefaultDatesValues = this.parseDefaultDatesValues({
                [`rows.${index}.dateDebut`]: dateDebut,
                [`rows.${index}.dateFin`]: dateFin,
              });
              const updatedDefaultDatesValues = {
                ...(accumulator?.defaultDatesValues ?? {}),
                ...newDefaultDatesValues,
              };
              const prejudiceRow = {
                dateDebut: dateDebut?.isoDate ?? null,
                dateFin: dateFin?.isoDate ?? null,
                percentageByClasse,
                percentage,
              } as const;
              return {
                ...(accumulator ?? {}),
                rows: [...(accumulator?.rows ?? []), prejudiceRow],
                defaultDatesValues: updatedDefaultDatesValues,
              };
            },
            {
              notes:
                extraction.prejudices.deficitFonctionnelTemporairePartiel
                  ?.resumePrejudice ?? '',
            },
          ) ?? {};
        const formData =
          calendrierDeficitFonctionnelTemporairePartielDefaultValues({
            values: defaultValues,
            procedure,
            isExtractedValues: true,
          });
        return {
          type: 'DEFICIT_FONCTIONNEL_TEMPORAIRE_PARTIEL',
          formType: 'CALENDRIER_DEFICIT_FONCTIONNEL_TEMPORAIRE_PARTIEL',
          formData,
        };
      }
      case 'deficitFonctionnelTemporaireTotal': {
        const defaultValues =
          extraction.prejudices.deficitFonctionnelTemporaireTotal?.rows.reduce<
            DFTTExistingValues>(
            (accumulator, row, index) => {
              const dateDebut = this.parseDate(row.dateDebut, {
                victime,
                procedure,
              });
              const dateFin = this.parseDate(row.dateFin, {
                victime,
                procedure,
              });
              const parsedRow: DFTTRowExistingValues = {
                dateDebut: dateDebut?.isoDate ?? null,
                dateFin: dateFin?.isoDate ?? null,
              };
              const newDefaultDatesValues = this.parseDefaultDatesValues({
                [`rows.${index}.dateDebut`]: dateDebut,
                [`rows.${index}.dateFin`]: dateFin,
              });
              const updatedDefaultDatesValues = {
                ...(accumulator?.defaultDatesValues ?? {}),
                ...newDefaultDatesValues,
              };
              return {
                ...(accumulator ?? {}),
                rows: [...(accumulator?.rows ?? []), parsedRow],
                defaultDatesValues: updatedDefaultDatesValues,
              };
            },
            {
              notes:
                extraction.prejudices.deficitFonctionnelTemporaireTotal
                  ?.resumePrejudice ?? '',
            },
          ) ?? {};
        const formData =
          calendrierDeficitFonctionnelTemporaireTotalDefaultValues({
            values: defaultValues,
            procedure,
            isExtractedValues: true,
          });
        return {
          type: 'DEFICIT_FONCTIONNEL_TEMPORAIRE_TOTAL',
          formType: 'CALENDRIER_DEFICIT_FONCTIONNEL_TEMPORAIRE_TOTAL',
          formData,
        };
      }
      case 'assistanceTiercePersonneTemporaire': {
        const defaultValues =
          extraction.prejudices.assistanceTiercePersonneTemporaire?.rows.reduce<CalendrierAssistanceExistingValues>(
            (accumulator, row, index) => {
              const parsedDateDebut = this.parseDate(row.dateDebut, {
                victime,
                procedure,
              });
              const dateDebut = parsedDateDebut?.isoDate ?? null;
              const parsedDateFin = this.parseDate(row.dateFin, {
                victime,
                procedure,
              });
              const dateFin = parsedDateFin?.isoDate ?? null;
              const uniteNombreHeureByFrequence: Record<
                NonNullable<NonNullable<typeof row.frequence>['type']>,
                AssistanceUniteNombreHeure
              > = {
                parMois: 'mois',
                parSemaine: 'semaine',
                parJour: 'jour',
              };
              const uniteNombreHeure = row.frequence?.type
                ? (uniteNombreHeureByFrequence[row.frequence.type] ?? undefined)
                : undefined;
              const nombreHeureParUniteChoisie =
                row.frequence?.heureParFrequence ?? undefined;
              const typeAssistance = this.parseTypeAssistance(row);
              const parsedRow: CalendrierAssistanceExistingRow = {
                dateDebut,
                dateFin,
                uniteNombreHeure,
                nombreHeureParUniteChoisie,
                typeAssistance,
                commentaires: row.natureAssistance,
              };
              const newDefaultDatesValues = this.parseDefaultDatesValues({
                [`rows.${index}.dateDebut`]: parsedDateDebut,
                [`rows.${index}.dateFin`]: parsedDateFin,
              });
              const updatedDefaultDatesValues = {
                ...(accumulator?.defaultDatesValues ?? {}),
                ...newDefaultDatesValues,
              };
              return {
                ...(accumulator ?? {}),
                rows: [...(accumulator?.rows ?? []), parsedRow],
                defaultDatesValues: updatedDefaultDatesValues,
              };
            },
            {
              notes:
                extraction.prejudices.assistanceTiercePersonneTemporaire
                  ?.resumePrejudice ?? '',
            },
          ) ?? {};
        const formData = calendrierAssistanceDefaultValues({
          procedure,
          values: defaultValues,
          isExtractedValues: true,
        });
        return {
          type: 'ASSISTANCE_PAR_TIERCE_PERSONNE_ACTUELLE',
          formType: 'CALENDRIER_ASSISTANCE',
          formData,
        };
      }
      case 'assistanceTiercePersonnePermanente': {
        const defaultValues =
          extraction.prejudices.assistanceTiercePersonnePermanente?.rows.reduce<CalendrierAssistanceCapitalisationExistingValues>(
            (
              accumulator,
              row,
              index,
            ) => {
              const parsedDateDebut = this.parseDate(row.dateDebut, {
                victime,
                procedure,
              });
              const dateDebut = parsedDateDebut?.isoDate ?? null;
              const parsedDateFin = this.parseDate(row.dateFin, {
                victime,
                procedure,
              });
              const dateFin = parsedDateFin?.isoDate ?? null;
              const uniteNombreHeureByFrequence: Record<
                NonNullable<NonNullable<typeof row.frequence>['type']>,
                AssistanceUniteNombreHeure
              > = {
                parMois: 'mois',
                parSemaine: 'semaine',
                parJour: 'jour',
              };
              const uniteNombreHeure = row.frequence?.type
                ? (uniteNombreHeureByFrequence[row.frequence.type] ?? undefined)
                : undefined;
              const nombreHeureParUniteChoisie =
                row.frequence?.heureParFrequence ?? undefined;
              const typeAssistance = this.parseTypeAssistance(row);
              const parsedRow: CalendrierAssistanceCapitalisationExistingRow = {
                dateDebut,
                dateFin,
                uniteNombreHeure,
                nombreHeureParUniteChoisie,
                typeAssistance,
                commentaires: row.natureAssistance,
              };
              const newDefaultDatesValues = this.parseDefaultDatesValues({
                [`rows.${index}.dateDebut`]: parsedDateDebut,
                [`rows.${index}.dateFin`]: parsedDateFin,
              });
              const updatedDefaultDatesValues = {
                ...(accumulator?.defaultDatesValues ?? {}),
                ...newDefaultDatesValues,
              };
              return {
                ...(accumulator ?? {}),
                rows: [...(accumulator?.rows ?? []), parsedRow],
                defaultDatesValues: updatedDefaultDatesValues,
              };
            },
            {
              notes:
                extraction.prejudices.assistanceTiercePersonnePermanente
                  ?.resumePrejudice ?? '',
              baremeCapitalisation: parsedBaremeCapitalisationId,
            },
          ) ?? {};
        const formData = calendrierAssistanceCapitalisationDefaultValues({
          monetaryErosions,
          baremes,
          victime,
          procedure,
          values: defaultValues,
          isExtractedValues: true,
        });
        return {
          type: 'ASSISTANCE_PAR_TIERCE_PERSONNE_FUTURE',
          formType: 'CALENDRIER_ASSISTANCE_CAPITALISATION',
          formData,
        };
      }
      case 'prejudiceScolaire': {
        const formData = scolaireDefaultValues({
          values: {
            notes:
              extraction.prejudices.prejudiceScolaire?.resumePrejudice ?? '',
          },
        });
        return {
          type: 'PREJUDICE_SCOLAIRE_UNIVERSITAIRE_FORMATION',
          formType: 'SCOLAIRE',
          formData,
        };
      }
      case 'incidenceProfessionnelle': {
        const formData = listeProjectionDefaultValues({
          values: {
            notes:
              extraction.prejudices.incidenceProfessionnelle?.resumePrejudice ??
              '',
            baremeCapitalisation: parsedBaremeCapitalisationId,
          },
          procedure,
          prejudiceType: 'INCIDENCE_PROFESSIONNELLE',
          baremes,
          dateConsolidation,
          dateLiquidation,
          isExtractedValues: true,
          PGPFReliquat,
        });
        return {
          type: 'INCIDENCE_PROFESSIONNELLE',
          formType: 'LISTE_PROJECTION',
          formData,
        };
      }
      case 'prejudiceAgrement': {
        const formData = listeProjectionDefaultValues({
          values: {
            notes:
              extraction.prejudices.prejudiceAgrement?.resumePrejudice ?? '',
            baremeCapitalisation: parsedBaremeCapitalisationId,
          },
          procedure,
          prejudiceType: 'PREJUDICE_AGREMENT',
          baremes,
          dateConsolidation,
          dateLiquidation,
          isExtractedValues: true,
          PGPFReliquat,
        });
        return {
          type: 'PREJUDICE_AGREMENT',
          formType: 'LISTE_PROJECTION',
          formData,
        };
      }
      case 'prejudiceSexuel': {
        const formData = listeProjectionDefaultValues({
          values: {
            notes:
              extraction.prejudices.prejudiceScolaire?.resumePrejudice ?? '',
            baremeCapitalisation: parsedBaremeCapitalisationId,
          },
          procedure,
          prejudiceType: 'PREJUDICE_SEXUEL',
          baremes,
          dateConsolidation,
          dateLiquidation,
          isExtractedValues: true,
          PGPFReliquat,
        });
        return {
          type: 'PREJUDICE_SEXUEL',
          formType: 'LISTE_PROJECTION',
          formData,
        };
      }
      case 'deficitFonctionnelPermanent': {
        const prejudiceValues: DfpExistingValues = {
          rows:
            extraction.prejudices.deficitFonctionnelPermanent?.rows.map(
              (row) => {
                const commentaires = row.natureDommageFonctionnel ?? undefined;
                const pourcentage = row.tauxDFP ?? undefined;
                return {
                  commentaires,
                  pourcentage,
                };
              },
            ) ?? [],
        };
        const formData = listeProjectionDefaultValues({
          values: {
            prejudiceValues,
            baremeCapitalisation: parsedBaremeCapitalisationId,
            notes:
              extraction.prejudices.deficitFonctionnelPermanent
                ?.resumePrejudice ?? '',
          },
          victime,
          procedure,
          baremes,
          prejudiceType: 'DEFICIT_FONCTIONNEL_PERMANENT',
          dateConsolidation,
          dateLiquidation,
          isExtractedValues: true,
          PGPFReliquat,
        });
        return {
          type: 'DEFICIT_FONCTIONNEL_PERMANENT',
          formType: 'LISTE_PROJECTION',
          formData,
        };
      }
      case 'prejudiceEsthetiquePermanent': {
        const prejudiceValues: MaladieExistingValues = {
          rows:
            extraction.prejudices.prejudiceEsthetiquePermanent?.rows.map(
              (row): NonNullable<MaladieExistingValues['rows']>[number] => {
                const maladie = row.naturePrejudiceEsthetique ?? undefined;
                const degre = this.parseDegre(row.degre);
                return {
                  maladie,
                  degre,
                };
              },
            ) ?? [],
        };
        const formData = listeProjectionDefaultValues({
          values: {
            prejudiceValues,
            baremeCapitalisation: parsedBaremeCapitalisationId,
            notes:
              extraction.prejudices.prejudiceEsthetiquePermanent
                ?.resumePrejudice ?? '',
          },
          victime,
          procedure,
          baremes,
          prejudiceType: 'PREJUDICE_ESTHETIQUE_PERMANENT',
          dateConsolidation,
          dateLiquidation,
          isExtractedValues: true,
          PGPFReliquat,
        });
        return {
          type: 'PREJUDICE_ESTHETIQUE_PERMANENT',
          formType: 'LISTE_PROJECTION',
          formData,
        };
      }
      case 'souffrancesEndurees': {
        const defaultValues =
          extraction.prejudices.souffrancesEndurees?.rows.reduce<ListeProjectionExistingValues>(
            (accumulator, row, index) => {
              const parsedDateDebut = this.parseDate(row.dateDebut, {
                victime,
                procedure,
              });
              const dateDebut = parsedDateDebut?.isoDate ?? null;
              const parsedDateFin = this.parseDate(row.dateFin, {
                victime,
                procedure,
              });
              const dateFin = parsedDateFin?.isoDate ?? null;
              const maladie = row.raisonSouffrance ?? undefined;
              const degre = this.parseDegre(row.degre);
              const parsedRow = {
                maladie,
                degre,
                dateDebut,
                dateFin,
              } as const;
              const newDefaultDatesValues = this.parseDefaultDatesValues({
                [`prejudiceValues.rows.${index}.dateDebut`]: parsedDateDebut,
                [`prejudiceValues.rows.${index}.dateFin`]: parsedDateFin,
              });
              const updatedDefaultDatesValues = {
                ...(accumulator?.defaultDatesValues ?? {}),
                ...newDefaultDatesValues,
              };
              const prejudiceValues: MaladieExistingValues = {
                rows: [
                  ...(accumulator?.prejudiceValues?.rows ?? []),
                  parsedRow,
                ],
              };
              return {
                ...(accumulator ?? {}),
                prejudiceValues,
                defaultDatesValues: updatedDefaultDatesValues,
              };
            },
            {
              notes:
                extraction.prejudices.souffrancesEndurees?.resumePrejudice ??
                '',
            },
          ) ?? {};
        const formData = listeProjectionDefaultValues({
          values: defaultValues,
          victime,
          procedure,
          baremes,
          prejudiceType: 'SOUFFRANCES_ENDUREES',
          dateConsolidation,
          dateLiquidation,
          isExtractedValues: true,
          PGPFReliquat,
        });
        return {
          type: 'SOUFFRANCES_ENDUREES',
          formType: 'LISTE_PROJECTION',
          formData,
        };
      }
      case 'prejudiceEsthetiqueTemporaire': {
        const defaultValues =
          extraction.prejudices.prejudiceEsthetiqueTemporaire?.rows.reduce<ListeProjectionExistingValues>(
            (accumulator, row, index) => {
              const parsedDateDebut = this.parseDate(row.dateDebut, {
                victime,
                procedure,
              });
              const dateDebut = parsedDateDebut?.isoDate ?? null;
              const parsedDateFin = this.parseDate(row.dateFin, {
                victime,
                procedure,
              });
              const dateFin = parsedDateFin?.isoDate ?? null;
              const maladie = row.naturePrejudiceEsthetique ?? undefined;
              const degre = this.parseDegre(row.degre);
              const parsedRow = {
                maladie,
                degre,
                dateDebut,
                dateFin,
              } as const;
              const newDefaultDatesValues = this.parseDefaultDatesValues({
                [`prejudiceValues.rows.${index}.dateDebut`]: parsedDateDebut,
                [`prejudiceValues.rows.${index}.dateFin`]: parsedDateFin,
              });
              const updatedDefaultDatesValues = {
                ...(accumulator?.defaultDatesValues ?? {}),
                ...newDefaultDatesValues,
              };
              const prejudiceValues: MaladieExistingValues = {
                rows: [
                  ...(accumulator?.prejudiceValues?.rows ?? []),
                  parsedRow,
                ],
              };
              return {
                ...(accumulator ?? {}),
                prejudiceValues,
                defaultDatesValues: updatedDefaultDatesValues,
              };
            },
            {
              notes:
                extraction.prejudices.prejudiceEsthetiqueTemporaire
                  ?.resumePrejudice ?? '',
            },
          ) ?? {};
        const formData = listeProjectionDefaultValues({
          values: defaultValues,
          victime,
          procedure,
          baremes,
          prejudiceType: 'PREJUDICE_ESTHETIQUE_TEMPORAIRE',
          dateConsolidation,
          dateLiquidation,
          isExtractedValues: true,
          PGPFReliquat,
        });
        return {
          type: 'PREJUDICE_ESTHETIQUE_TEMPORAIRE',
          formType: 'LISTE_PROJECTION',
          formData,
        };
      }
      case 'pertesDeGainsProfessionnelsFuturs': {
        const formData = pgpfSaisieDirecteDefaultValues({
          values: {
            notes:
              extraction.prejudices.pertesDeGainsProfessionnelsFuturs
                ?.resumePrejudice ?? '',
            capitalisation: {
              bareme: parsedBaremeCapitalisationId,
            },
          },
          procedure,
        });
        return {
          type: 'PERTES_GAINS_PROFESSIONNELS_FUTURS',
          formType: 'PERTE_GAINS_PROFESSIONNELS_FUTURS',
          formData,
        };
      }
      case 'pertesDeGainsProfessionnelsActuels': {
        const defaultValues =
          extraction.prejudices.pertesDeGainsProfessionnelsActuels?.arretsDeTravail.reduce<PGPExistingValues>(
            (accumulator, arret, index) => {
              const parsedDateDebut = this.parseDate(arret.dateDebut, {
                victime,
                procedure,
              });
              const dateDebut = parsedDateDebut?.isoDate ?? null;
              const parsedDateFin = this.parseDate(arret.dateFin, {
                victime,
                procedure,
              });
              const dateFin = parsedDateFin?.isoDate ?? null;
              const parsedPerteDeGainsProfessionnelsRow: PerteDeGainsProfessionnelsRowExistingValues =
                {
                  commentaires: arret.commentaires ?? '',
                  dateDebut,
                  dateFin,
                  montantsDejaRevalorises: false,
                  numerosPieces: { rows: [] },
                  // TODO : Check if we can set them to 0 without impact
                  duree: null,
                  revenuPercuNet: 0,
                  revenuDeReferenceAnnuel: 0,
                  revenuDeReferencePeriode: 0,
                  revenuPercuParPeriode: 0,
                  perteDeGainsProfessionnels: 0,
                  unite: '',
                };
              const newDefaultDatesValues = this.parseDefaultDatesValues({
                [`rows.${index}.dateDebut`]: parsedDateDebut,
                [`rows.${index}.dateFin`]: parsedDateFin,
              });
              const updatedDefaultDatesValues = {
                ...(accumulator?.defaultDatesValues ?? {}),
                ...newDefaultDatesValues,
              };
              return {
                ...(accumulator ?? {}),
                perteDeGainsProfessionnels: {
                  rows: [
                    ...(accumulator?.perteDeGainsProfessionnels?.rows ?? []),
                    parsedPerteDeGainsProfessionnelsRow,
                  ],
                },
                defaultDatesValues: updatedDefaultDatesValues,
              };
            },
            {
              notes:
                extraction.prejudices.pertesDeGainsProfessionnelsActuels
                  ?.resumePrejudice ?? '',
            },
          ) ?? {};
        const formData = pgpaSaisieDirecteDefaultValues({
          values: defaultValues,
        });
        return {
          type: 'PERTES_GAINS_PROFESSIONNELS_ACTUELS',
          formType: 'PERTE_GAINS_PROFESSIONNELS_ACTUEL',
          formData,
        };
      }
      case 'depensesSanteActuelles': {
        const defaultValues =
          extraction.prejudices.depensesSanteActuelles?.rows.reduce<CalendrierDepenseExistingValues>(
            (accumulator, row, index) => {
              const type =
                row.typeDepense === 'depenseRecurrente'
                  ? 'recurrente'
                  : 'ponctuelle';
              const intitule = row.intitule ?? undefined;
              const parsedDateDepense = this.parseDate(row.dateDepense, {
                victime,
                procedure,
              });
              const dateDepense = parsedDateDepense?.isoDate ?? null;
              const parsedDateDebut = this.parseDate(row.dateDebut, {
                victime,
                procedure,
              });
              const dateDebut = parsedDateDebut?.isoDate ?? null;
              const parsedDateFin = this.parseDate(row.dateFin, {
                victime,
                procedure,
              });
              const dateFin = parsedDateFin?.isoDate ?? null;

              const frequenceTypeMap: Record<
                | 'parJour'
                | 'parSemaine'
                | 'parMois'
                | 'parAnnee'
                | 'ARenouvelerTousLesXAnnees',
                DepenseFrequence
              > = {
                parJour: 'quotidien',
                parSemaine: 'hebdomadaire',
                parMois: 'mensuel',
                parAnnee: 'annuel',
                ARenouvelerTousLesXAnnees: 'viagere',
              };
              const frequenceMontant: DepenseFrequence =
                row.frequence && row.frequence.type
                  ? frequenceTypeMap[row.frequence.type]
                  : 'non_renseigne';
              const parsedRow: CalendrierDepenseExistingRow = {
                type,
                intitule,
                dateDebut: dateDebut ?? dateDepense,
                dateFin,
                frequenceMontant,
              };
              const newDefaultDatesValues = this.parseDefaultDatesValues({
                [`rows.${index}.dateDebut`]: parsedDateDebut,
                [`rows.${index}.dateFin`]: parsedDateFin,
              });
              const updatedDefaultDatesValues = {
                ...(accumulator?.defaultDatesValues ?? {}),
                ...newDefaultDatesValues,
              };
              return {
                ...(accumulator ?? {}),
                rows: [...(accumulator?.rows ?? []), parsedRow],
                defaultDatesValues: updatedDefaultDatesValues,
              };
            },
            {
              notes:
                extraction.prejudices.depensesSanteActuelles?.resumePrejudice ??
                '',
            },
          ) ?? {};
        const formData = calendrierDepenseDefaultValues({
          values: defaultValues,
          procedure,
          victime,
          multiplyPartResponsabilite: true,
          hidePeriode: false,
          isVictimeIndirecte: false,
        });
        return {
          type: 'DEPENSE_SANTE_ACTUELLES',
          formType: 'CALENDRIER_DEPENSE',
          formData,
        };
      }
      case 'depensesSanteFutures': {
        const defaultValues =
          extraction.prejudices.depensesSanteFutures?.rows.reduce<CalendrierDepenseCapitalisationExistingValues>(
            (
              accumulator,
              row,
              index,
            ) => {
              const type =
                row.typeDepense === 'depenseRecurrente'
                  ? 'recurrente'
                  : 'ponctuelle';
              const intitule = row.intitule ?? undefined;
              const parsedDateDepense = this.parseDate(row.dateDepense, {
                victime,
                procedure,
              });
              const dateDepense = parsedDateDepense?.isoDate ?? null;
              const parsedDateDebut = this.parseDate(row.dateDebut, {
                victime,
                procedure,
              });
              const dateDebut = parsedDateDebut?.isoDate ?? null;
              const parsedDateFin = this.parseDate(row.dateFin, {
                victime,
                procedure,
              });
              const dateFin = parsedDateFin?.isoDate ?? null;

              const frequenceTypeMap: Record<
                | 'parJour'
                | 'parSemaine'
                | 'parMois'
                | 'parAnnee'
                | 'ARenouvelerTousLesXAnnees',
                DepenseFrequence
              > = {
                parJour: 'quotidien',
                parSemaine: 'hebdomadaire',
                parMois: 'mensuel',
                parAnnee: 'annuel',
                ARenouvelerTousLesXAnnees: 'viagere',
              };
              const frequenceMontant: DepenseFrequence =
                row.frequence && row.frequence.type
                  ? frequenceTypeMap[row.frequence.type]
                  : 'non_renseigne';

              const parsedRow: CalendrierDepenseCapitalisationExistingRow = {
                type,
                intitule,
                dateDebut: dateDebut ?? dateDepense,
                dateFin,
                frequenceMontant,
              };
              const newDefaultDatesValues = this.parseDefaultDatesValues({
                [`rows.${index}.dateDebut`]: parsedDateDebut,
                [`rows.${index}.dateFin`]: parsedDateFin,
              });
              const updatedDefaultDatesValues = {
                ...(accumulator?.defaultDatesValues ?? {}),
                ...newDefaultDatesValues,
              };
              return {
                ...(accumulator ?? {}),
                rows: [...(accumulator?.rows ?? []), parsedRow],
                defaultDatesValues: updatedDefaultDatesValues,
              };
            },
            {
              notes:
                extraction.prejudices.depensesSanteFutures?.resumePrejudice ??
                '',
              capitalisation: {
                bareme: parsedBaremeCapitalisationId,
              },
            },
          ) ?? {};
        const formData = calendrierDepenseCapitalisationDefaultValues({
          values: defaultValues,
          procedure,
          victime,
          isExtractedValues: true,
        });
        return {
          type: 'DEPENSE_SANTE_FUTURES',
          formType: 'CALENDRIER_DEPENSE_CAPITALISATION',
          formData,
        };
      }
      case 'fraisLogementAdapte': {
        const defaultValues =
          extraction.prejudices.fraisLogementAdapte?.rows.reduce<CalendrierDepenseCapitalisationExistingValues>(
            (
              accumulator,
              row,
              index,
            ) => {
              const type =
                row.typeDepense === 'depenseRecurrente'
                  ? 'recurrente'
                  : 'ponctuelle';
              const intitule = row.intitule ?? undefined;
              const parsedDateDepense = this.parseDate(row.dateDepense, {
                victime,
                procedure,
              });
              const dateDepense = parsedDateDepense?.isoDate ?? null;
              const parsedDateDebut = this.parseDate(row.dateDebut, {
                victime,
                procedure,
              });
              const dateDebut = parsedDateDebut?.isoDate ?? null;
              const parsedDateFin = this.parseDate(row.dateFin, {
                victime,
                procedure,
              });
              const dateFin = parsedDateFin?.isoDate ?? null;

              const frequenceTypeMap: Record<
                | 'parJour'
                | 'parSemaine'
                | 'parMois'
                | 'parAnnee'
                | 'ARenouvelerTousLesXAnnees',
                DepenseFrequence
              > = {
                parJour: 'quotidien',
                parSemaine: 'hebdomadaire',
                parMois: 'mensuel',
                parAnnee: 'annuel',
                ARenouvelerTousLesXAnnees: 'viagere',
              };
              const frequenceMontant: DepenseFrequence =
                row.frequence && row.frequence.type
                  ? frequenceTypeMap[row.frequence.type]
                  : 'non_renseigne';

              const parsedRow: CalendrierDepenseCapitalisationExistingRow = {
                type,
                intitule,
                dateDebut: dateDebut ?? dateDepense,
                dateFin,
                frequenceMontant,
              };
              const newDefaultDatesValues = this.parseDefaultDatesValues({
                [`rows.${index}.dateDebut`]: parsedDateDebut,
                [`rows.${index}.dateFin`]: parsedDateFin,
              });
              const updatedDefaultDatesValues = {
                ...(accumulator?.defaultDatesValues ?? {}),
                ...newDefaultDatesValues,
              };
              return {
                ...(accumulator ?? {}),
                rows: [...(accumulator?.rows ?? []), parsedRow],
                defaultDatesValues: updatedDefaultDatesValues,
              };
            },
            {
              notes:
                extraction.prejudices.fraisLogementAdapte?.resumePrejudice ??
                '',
              capitalisation: {
                bareme: parsedBaremeCapitalisationId,
              },
            },
          ) ?? {};
        const formData = calendrierDepenseCapitalisationDefaultValues({
          values: defaultValues,
          procedure,
          victime,
          isExtractedValues: true,
        });
        return {
          type: 'FRAIS_LOGEMENT_ADAPTE',
          formType: 'CALENDRIER_DEPENSE_CAPITALISATION',
          formData,
        };
      }
      case 'fraisVehiculeAdapte': {
        const defaultValues =
          extraction.prejudices.fraisVehiculeAdapte?.rows.reduce<CalendrierDepenseCapitalisationExistingValues>(
            (
              accumulator,
              row,
              index,
            ) => {
              const type =
                row.typeDepense === 'depenseRecurrente'
                  ? 'recurrente'
                  : 'ponctuelle';
              const intitule = row.intitule ?? undefined;
              const parsedDateDepense = this.parseDate(row.dateDepense, {
                victime,
                procedure,
              });
              const dateDepense = parsedDateDepense?.isoDate ?? null;
              const parsedDateDebut = this.parseDate(row.dateDebut, {
                victime,
                procedure,
              });
              const dateDebut = parsedDateDebut?.isoDate ?? null;
              const parsedDateFin = this.parseDate(row.dateFin, {
                victime,
                procedure,
              });
              const dateFin = parsedDateFin?.isoDate ?? null;

              const frequenceTypeMap: Record<
                | 'parJour'
                | 'parSemaine'
                | 'parMois'
                | 'parAnnee'
                | 'ARenouvelerTousLesXAnnees',
                DepenseFrequence
              > = {
                parJour: 'quotidien',
                parSemaine: 'hebdomadaire',
                parMois: 'mensuel',
                parAnnee: 'annuel',
                ARenouvelerTousLesXAnnees: 'viagere',
              };
              const frequenceMontant: DepenseFrequence =
                row.frequence && row.frequence.type
                  ? frequenceTypeMap[row.frequence.type]
                  : 'non_renseigne';

              const parsedRow = {
                type,
                intitule,
                dateDebut: dateDebut ?? dateDepense,
                dateFin,
                frequenceMontant,
              } as const;
              const newDefaultDatesValues = this.parseDefaultDatesValues({
                [`rows.${index}.dateDebut`]: parsedDateDebut,
                [`rows.${index}.dateFin`]: parsedDateFin,
              });
              const updatedDefaultDatesValues = {
                ...(accumulator?.defaultDatesValues ?? {}),
                ...newDefaultDatesValues,
              };
              return {
                ...(accumulator ?? {}),
                rows: [...(accumulator?.rows ?? []), parsedRow],
                defaultDatesValues: updatedDefaultDatesValues,
              };
            },
            {
              notes:
                extraction.prejudices.fraisVehiculeAdapte?.resumePrejudice ??
                '',
              capitalisation: {
                bareme: parsedBaremeCapitalisationId,
              },
            },
          ) ?? {};
        const formData = calendrierDepenseCapitalisationDefaultValues({
          values: defaultValues,
          procedure,
          victime,
          isExtractedValues: true,
        });
        return {
          type: 'FRAIS_VEHICULE_ADAPTE',
          formType: 'CALENDRIER_DEPENSE_CAPITALISATION',
          formData,
        };
      }
      case 'prejudiceEtablissement': {
        const formData = valeurDefaultValues({
          values: {
            notes:
              extraction.prejudices.prejudiceEtablissement?.resumePrejudice ??
              '',
          },
          procedurePartResponsabilite: procedure.partResponsabilite,
          multiplyPartResponsabilite: true,
          prejudiceType: 'PREJUDICE_ETABLISSEMENT',
        });
        return {
          type: 'PREJUDICE_ETABLISSEMENT',
          formType: 'VALEUR',
          formData,
        };
      }
      case 'prejudiceAnxiete': {
        const formData = valeurDefaultValues({
          values: {
            notes:
              extraction.prejudices.prejudiceAnxiete?.resumePrejudice ?? '',
          },
          procedurePartResponsabilite: procedure.partResponsabilite,
          multiplyPartResponsabilite: true,
          prejudiceType: 'PREJUDICE_ANXIETE',
        });
        return {
          type: 'PREJUDICE_ANXIETE',
          formType: 'VALEUR',
          formData,
        };
      }
      default:
        return null;
    }
  };
  async parseExtractionPrejudices(
    victime: Victime,
    procedure: Procedure,
    extractionOutput: ExtractionOutputType,
    baremes: Bareme[],
    monetaryErosions: MonetaryErosion[],
  ) {
    const parsedPrejudices = Object.entries(extractionOutput.prejudices)
      .filter(([_, prejudice]) => prejudice !== null)
      .map(([label]) =>
        this.parseExtractionPrejudice(
          procedure,
          label as PrejudiceLabel,
          extractionOutput,
          baremes,
          victime,
          monetaryErosions,
        ),
      )
      .filter((prejudice): prejudice is CreatePrejudiceDto => !!prejudice);
    return parsedPrejudices;
  }
}
